import type {
  ReportDraft,
  ReportDraftInput,
  ReportMoneyKey,
  ReportRowInput,
} from "./reportDraftApi";

export const moneyFields: { key: ReportMoneyKey; label: string }[] = [
  { key: "stockNet", label: "Хувьцааны цэвэр дүн" },
  { key: "bondNet", label: "Бондын цэвэр дүн" },
  { key: "estateNet", label: "Үл хөдлөхийн цэвэр дүн" },
  { key: "cashAmount", label: "Бэлэн мөнгө" },
  { key: "bankFee", label: "Банкны шимтгэл" },
];

export const sourceLabels = {
  Confirmation: "Баталгаажсан дүн",
  RequestSnapshot: "Хүсэлтэд хадгалсан эх",
  UserBank: "Бүртгэлтэй банк",
  DraftInput: "Гараар нөхсөн · Нооргийн дүн",
  ExcelImport: "Excel-ээс нөхсөн · Тайлангийн дүн",
  NotApplicable: "Хамаарахгүй",
  Missing: "Эх дутуу",
};

export const withdrawalStatusLabels: Record<string, string> = {
  Pending: "Хүсэлт хүлээгдэж буй",
  Processing: "Хүлээгдэж буй",
  TransferPending: "Шилжүүлэг хийж буй",
  Done: "Баталгаажсан",
  Canceled: "Цуцлагдсан",
};

export const historyActionLabels: Record<string, string> = {
  Save: "Ноорог хадгалсан",
  Refresh: "Эхийг шинэчилсэн",
  Submit: "Хяналтад илгээсэн",
  Import: "Excel-ээс дутуу утга нөхсөн",
  ApplyImport: "Excel-ээс дутуу утга нөхсөн",
  Approve: "Тайлан баталсан",
  Return: "Засварт буцаасан",
};

export function moneyText(value: string | null): string {
  if (value === null) return "Дутуу";
  const [whole, fraction = ""] = value.split(".");
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction.padEnd(2, "0")} ₮`;
}

export function isValidMoney(value: string | null | undefined): boolean {
  return (
    value == null ||
    value === "" ||
    /^(0|[1-9]\d{0,11})(\.\d{1,2})?$/.test(value)
  );
}

export function copyInput(input: ReportDraftInput): ReportDraftInput {
  return {
    asOfDate: input.asOfDate,
    note: input.note ?? null,
    rows: input.rows.map((row) => ({ ...row })),
  };
}

export function sameInput(
  left: ReportDraftInput,
  right: ReportDraftInput,
): boolean {
  const normalize = (input: ReportDraftInput) => ({
    asOfDate: input.asOfDate,
    note: input.note || null,
    rows: input.rows
      .map((row) => ({
        requestId: row.requestId,
        ...Object.fromEntries(
          [
            ...moneyFields.map(({ key }) => key),
            "bankName",
            "accountNo",
            "sourceNote",
          ].map((key) => [key, row[key as keyof ReportRowInput] || null]),
        ),
      }))
      .filter((row) =>
        Object.entries(row).some(
          ([key, value]) => key !== "requestId" && value !== null,
        ),
      )
      .sort((a, b) => a.requestId.localeCompare(b.requestId)),
  });
  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
}

// A source refresh persists server input. Reapply only unsaved edits that remain
// editable, so new confirmations/bank details always take precedence.
export function rebaseInput(
  previous: ReportDraft,
  edited: ReportDraftInput,
  next: ReportDraft,
): { input: ReportDraftInput; dropped: number } {
  const result = copyInput(next.input);
  let dropped = 0;
  if (next.state !== "Draft")
    return { input: result, dropped: sameInput(edited, next.input) ? 0 : 1 };
  if (edited.asOfDate !== previous.input.asOfDate)
    result.asOfDate = edited.asOfDate;
  if ((edited.note ?? null) !== (previous.input.note ?? null))
    result.note = edited.note;
  const keys = [
    ...moneyFields.map(({ key }) => key),
    "bankName",
    "accountNo",
    "sourceNote",
  ] as const;
  for (const editedRow of edited.rows) {
    const old = previous.input.rows.find(
      (row) => row.requestId === editedRow.requestId,
    );
    const nextRow = next.rows.find(
      (row) => row.requestId === editedRow.requestId,
    );
    let inputRow = result.rows.find(
      (row) => row.requestId === editedRow.requestId,
    );
    for (const key of keys) {
      if ((editedRow[key] ?? null) === (old?.[key] ?? null)) continue;
      const locked =
        !nextRow ||
        (key === "bankName"
          ? nextRow.bankNameLocked
          : key === "accountNo"
            ? nextRow.accountNoLocked
            : key === "sourceNote"
              ? false
              : nextRow[key].locked);
      if (locked) {
        dropped++;
        continue;
      }
      if (!inputRow) {
        inputRow = { requestId: editedRow.requestId };
        result.rows.push(inputRow);
      }
      inputRow[key] = editedRow[key] ?? null;
    }
  }
  return { input: result, dropped };
}

export function reportAccess(
  report: ReportDraft,
  latest: ReportDraft | undefined,
  options: { canEdit: boolean; dirty: boolean; busy: boolean },
) {
  const remoteVersionChanged = !!latest && latest.version !== report.version;
  const sourceChanged = report.sourceChanged || !!latest?.sourceChanged;
  const editable =
    options.canEdit &&
    report.state === "Draft" &&
    (!latest || latest.state === "Draft");
  return {
    remoteVersionChanged,
    sourceChanged,
    editable,
    canSubmit:
      editable &&
      report.version > 0 &&
      report.canSubmit &&
      !options.dirty &&
      !sourceChanged &&
      !remoteVersionChanged &&
      !options.busy,
  };
}

export function isCompletedReportBatch(status: string): boolean {
  return status === "Done" || status === "TransferPending";
}

export function reportImportAccess(
  report: ReportDraft,
  latest: ReportDraft | undefined,
  options: { canEdit: boolean; dirty: boolean },
) {
  const editable =
    options.canEdit &&
    report.state === "Draft" &&
    (!latest || latest.state === "Draft");
  const current =
    !report.sourceChanged &&
    !latest?.sourceChanged &&
    (!latest || latest.version === report.version);
  const eligible =
    editable &&
    current &&
    !options.dirty &&
    isCompletedReportBatch(report.batchStatus);
  const blockedReason = !editable
    ? "Тайлангийн энэ төлөв эсвэл таны эрхээр файл импортлох боломжгүй."
    : !isCompletedReportBatch(report.batchStatus)
      ? "Энэ импорт нь шилжүүлэг хийж буй эсвэл дууссан хуучин багцад зориулагдсан."
      : options.dirty
        ? "Хадгалаагүй өөрчлөлтөө эхлээд хадгална уу."
        : !current
          ? "Эх өгөгдөл / хувилбар өөрчлөгдсөн. Эхлээд тайланг шинэчилж хянана уу."
          : null;
  return { editable, eligible, blockedReason };
}

export function reportReviewAccess(
  report: ReportDraft,
  latest: ReportDraft | undefined,
  options: { isAdmin: boolean; dirty: boolean; busy: boolean },
) {
  const current =
    !latest ||
    (latest.version === report.version && latest.state === report.state);
  const canReturn =
    options.isAdmin &&
    report.state === "InReview" &&
    current &&
    !options.dirty &&
    !options.busy;
  return {
    canReturn,
    canApprove:
      canReturn &&
      isCompletedReportBatch(report.batchStatus) &&
      report.canApprove &&
      !report.sourceChanged &&
      !latest?.sourceChanged,
  };
}

export function reportError(error: unknown): string {
  const graphql = error as { response?: { errors?: { message?: string }[] } };
  return (
    graphql?.response?.errors
      ?.map((item) => item.message)
      .filter(Boolean)
      .join(". ") || "Тайлантай ажиллахад алдаа гарлаа. Дахин оролдоно уу."
  );
}
